import { Store } from 'Application/Store/Store'
import { threeEngine } from 'Domain/Scene/Engine/ThreeEngine'
import { getViewportMode, getViewports, getIsPortrait } from 'Domain/Scene/Handler/ViewportHandler'
import { isVerticalSplit } from 'Domain/Scene/Handler/viewportLayout'

let mouseX = 0;
let mouseY = 0;
let isPointerDown = false;
let activeViewportOnDown = null;

const getViewportAtPoint = (clientX, clientY) => {
  const viewports = getViewports()
  if (!viewports || viewports.length === 0) return null

  const renderer = threeEngine.getRenderer()
  if (!renderer || !renderer.domElement) return viewports[0]?.name

  const rect = renderer.domElement.getBoundingClientRect()
  const vertical = isVerticalSplit(viewports.length, getIsPortrait())
  const canvasSize = vertical ? rect.height : rect.width
  if (!canvasSize || canvasSize < 1) return viewports[0].name

  const pointerPos = vertical ? clientY : clientX
  const rectStart = vertical ? rect.top : rect.left
  const relativePos = pointerPos - rectStart

  if (relativePos < 0) return viewports[0].name
  if (relativePos >= canvasSize) return viewports[viewports.length - 1].name

  const sectionSize = canvasSize / viewports.length
  let index = Math.floor(relativePos / sectionSize)
  if (index >= viewports.length) index = viewports.length - 1
  if (index < 0) index = 0
  return viewports[index].name
}

window.addEventListener('pointermove', (e) => {
  mouseX = e.clientX;
  mouseY = e.clientY;
});

window.addEventListener('pointerdown', (e) => {
  isPointerDown = true;
  // Lock the active viewport at the start of the gesture
  const viewportMode = getViewportMode()
  if (viewportMode === 'double' || viewportMode === 'triple') {
    activeViewportOnDown = getViewportAtPoint(e.clientX, e.clientY)
  }
});

window.addEventListener('pointerup', () => {
  isPointerDown = false;
  activeViewportOnDown = null;
});

window.addEventListener('pointercancel', () => {
  isPointerDown = false;
  activeViewportOnDown = null;
});

const setBooleanControlProperty = (controls, prop, value) => {
  if (typeof controls[prop] !== 'undefined') {
    controls[prop] = value
  }
}

const applyControlInteractionRules = (controls, viewportMode) => {
  const isDoubleView = viewportMode === 'double'
  const isTripleView = viewportMode === 'triple'
  const isSingleView = viewportMode === 'single'
  const isEditMode = Store.getState().ViewportReducer.mode === 'edition'

  let disableRotate = isDoubleView
  let disableOrbitPan = isDoubleView ? false : true
  let disableTrackballPan = false

  if (isTripleView) {
     const activeViewportName = controls.object?.view || controls.object?.name?.toLowerCase() || 'front'
     if (activeViewportName.includes('bottom') || activeViewportName.includes('top')) {
         disableRotate = true
         disableOrbitPan = false
         disableTrackballPan = false
     } else {
         disableRotate = false
         disableOrbitPan = false
         disableTrackballPan = false
     }
  } else if (isSingleView && !isEditMode) {
     disableRotate = false
     disableOrbitPan = false
     disableTrackballPan = false
  } else if (viewportMode === 'comparative') {
     disableRotate = false
     disableOrbitPan = false
     disableTrackballPan = false
  }

  setBooleanControlProperty(controls, 'enableRotate', !disableRotate)
  setBooleanControlProperty(controls, 'enablePan', !disableOrbitPan)
  setBooleanControlProperty(controls, 'enableZoom', true)

  setBooleanControlProperty(controls, 'noRotate', disableRotate)
  setBooleanControlProperty(controls, 'noPan', disableTrackballPan)
  setBooleanControlProperty(controls, 'noZoom', false)
}

const syncDoubleViewOrthographicZoom = (controls) => {
  // Sur layouts mobiles (portrait + mobile paysage), les 2 vues sont étroites
  // → chaque vue garde son propre zoom
  const globalLayout = Store.getState().LayoutReducer?.global
  const isMobileLayout = !!(globalLayout?.isPortrait || globalLayout?.isMobileLandscape)
  if (isVerticalSplit(2, getIsPortrait()) || isMobileLayout) {
    return
  }

  const bottomCamera = threeEngine.getCamera('bottom')
  const topCamera = threeEngine.getCamera('top')
  const sourceCamera = controls.object || bottomCamera

  if (!bottomCamera?.isOrthographicCamera || !topCamera?.isOrthographicCamera || !sourceCamera?.isOrthographicCamera) {
    return
  }

  const sharedZoom = sourceCamera.zoom
  if (bottomCamera.zoom !== sharedZoom) {
    bottomCamera.zoom = sharedZoom
    bottomCamera.updateProjectionMatrix()
  }
  if (topCamera.zoom !== sharedZoom) {
    topCamera.zoom = sharedZoom
    topCamera.updateProjectionMatrix()
  }
}

export const syncOrbitControlState = () => {
  threeEngine.updateControls()
  const controls = threeEngine.getControls()
  if (!controls) return

  const viewportMode = getViewportMode()
  controls.autoRotate = Store.getState().AnimationReducer.autoRotate

  applyControlInteractionRules(controls, viewportMode)

  if (viewportMode === 'double') {
    syncDoubleViewOrthographicZoom(controls)
  }
}

const getActiveViewportName = (viewportMode) => {
  // Si un geste est en cours, on locke sur la vue capturée au pointerdown
  if (isPointerDown && activeViewportOnDown) {
    return activeViewportOnDown
  }

  const viewports = getViewports()
  if (!viewports || viewports.length === 0) return 'main'

  const renderer = threeEngine.getRenderer()
  if (!renderer || !renderer.domElement) return viewports[0].name

  const rect = renderer.domElement.getBoundingClientRect()
  const vertical = isVerticalSplit(viewports.length, getIsPortrait())
  const canvasSize = vertical ? rect.height : rect.width
  if (!canvasSize || canvasSize < 1) return viewports[0].name

  const pointerPos = vertical ? mouseY : mouseX
  const rectStart = vertical ? rect.top : rect.left
  const relativePos = pointerPos - rectStart

  if (relativePos < 0) return viewports[0].name
  if (relativePos >= canvasSize) return viewports[viewports.length - 1].name

  const sectionSize = canvasSize / viewports.length
  let index = Math.floor(relativePos / sectionSize)
  if (index >= viewports.length) index = viewports.length - 1
  if (index < 0) index = 0
  return viewports[index].name
}

export const updateOrbitControlCamera = () => {
  const viewportMode = getViewportMode()
  const activeViewportName = getActiveViewportName(viewportMode)

  let targetCamera = threeEngine.getCamera(activeViewportName)
  if (viewportMode === 'comparative') {
      targetCamera = threeEngine.getCamera('main')
  }

  if (!targetCamera) {
    const isTripleView = viewportMode === 'triple'
    const isDoubleView = viewportMode === 'double'
    targetCamera = threeEngine.getCamera('main')
    if (isTripleView) targetCamera = threeEngine.getCamera('front')
    if (isDoubleView) targetCamera = threeEngine.getCamera('bottom')
  }

  const controls = threeEngine.getControls()
  if (controls && targetCamera) {
    if (controls.object !== targetCamera) {
        if (controls.object) {
            controls.object.userData.target = controls.target.clone()
            
            const wasDamping = controls.enableDamping;
            if (wasDamping) {
                controls.enableDamping = false;
                controls.update(); // applies remaining momentum immediately
                controls.enableDamping = true;
                controls.object.userData.target = controls.target.clone();
            }
        }
        
        controls.object = targetCamera
        
        if (targetCamera.userData && targetCamera.userData.target) {
            controls.target.copy(targetCamera.userData.target)
        } else {
            const center = threeEngine.getCenter()
            if (center) controls.target.copy(center)
        }
    }
  }
}
